import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

// Import Azure Speech SDK
declare var SpeechSDK: any;

export interface SpeechRecognitionResult {
  text: string;
  isFinal: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AzureSpeechService {
  private speechConfig: any = null;
  private audioConfig: any = null;
  private recognizer: any = null;
  private isInitialized: boolean = false;
  private isRecognizing: boolean = false;

  // Subjects for emitting recognition results
  private recognitionResultSubject = new Subject<SpeechRecognitionResult>();
  public recognitionResult$: Observable<SpeechRecognitionResult> = this.recognitionResultSubject.asObservable();

  private recognitionErrorSubject = new Subject<string>();
  public recognitionError$: Observable<string> = this.recognitionErrorSubject.asObservable();

  private recognitionEndedSubject = new Subject<void>();
  public recognitionEnded$: Observable<void> = this.recognitionEndedSubject.asObservable();

  constructor() {
    // Wait a bit for the SpeechSDK script to load, then initialize
    this.waitForSpeechSDKAndInitialize();
  }

  /**
   * Wait for SpeechSDK to load, then initialize
   */
  private async waitForSpeechSDKAndInitialize(): Promise<void> {
    if (environment.DISABLE_BACKEND) {
      return;
    }
    // Wait up to 5 seconds for SpeechSDK to load
    let attempts = 0;
    const maxAttempts = 50; // 50 attempts * 100ms = 5 seconds
    
    while (attempts < maxAttempts) {
      if (typeof SpeechSDK !== 'undefined') {
        await this.initialize();
        return;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    // If SpeechSDK still not loaded, try to initialize anyway (might work on next check)
    console.warn('SpeechSDK not loaded after waiting, will retry on first use');
    await this.initialize();
  }

  /**
   * Initialize Azure Speech SDK
   */
  private async initialize(): Promise<void> {
    try {
      // Check if Azure Speech configuration is available
      if (!environment.AZURE_SPEECH || !environment.AZURE_SPEECH.subscriptionKey || !environment.AZURE_SPEECH.region) {
        console.warn('Azure Speech Service configuration is missing. Please add subscriptionKey and region to environment.');
        return;
      }

      // Check if Speech SDK is loaded
      if (typeof SpeechSDK === 'undefined') {
        console.warn('Azure Speech SDK is not loaded yet. It may still be loading from CDN.');
        return;
      }

      // Create speech configuration
      this.speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
        environment.AZURE_SPEECH.subscriptionKey,
        environment.AZURE_SPEECH.region
      );

      // Set language
      this.speechConfig.speechRecognitionLanguage = environment.AZURE_SPEECH.language || 'en-US';

      // Create audio configuration (use default microphone)
      this.audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();

      this.isInitialized = true;
      console.log('Azure Speech Service initialized successfully');
    } catch (error) {
      console.error('Error initializing Azure Speech Service:', error);
      this.recognitionErrorSubject.next('Failed to initialize Azure Speech Service');
    }
  }

  /**
   * Check if Azure Speech Service is available and configured
   */
  public isAvailable(): boolean {
    if (environment.DISABLE_BACKEND) {
      return false;
    }
    // Check configuration first
    if (!environment.AZURE_SPEECH?.subscriptionKey || !environment.AZURE_SPEECH?.region) {
      return false;
    }

    // If SpeechSDK is now loaded but service wasn't initialized, try to initialize now
    if (typeof SpeechSDK !== 'undefined' && !this.isInitialized) {
      this.initialize();
    }

    return this.isInitialized && 
           typeof SpeechSDK !== 'undefined';
  }

  /**
   * Set the recognition language
   */
  public setLanguage(language: string): void {
    if (this.speechConfig) {
      this.speechConfig.speechRecognitionLanguage = language;
      console.log(`Speech recognition language set to: ${language}`);
    }
  }

  /**
   * Start continuous speech recognition
   * @param language Optional language code (e.g., 'en-US', 'ar-SA'). If not provided, uses default from environment.
   */
  public async startRecognition(language?: string): Promise<void> {
    if (!this.isAvailable()) {
      const errorMsg = 'Azure Speech Service is not available. Please check configuration.';
      console.error(errorMsg);
      this.recognitionErrorSubject.next(errorMsg);
      return;
    }

    if (this.isRecognizing) {
      console.warn('Speech recognition is already running');
      return;
    }

    try {
      // Set language if provided
      if (language) {
        this.setLanguage(language);
      }

      // Create recognizer
      this.recognizer = new SpeechSDK.SpeechRecognizer(this.speechConfig, this.audioConfig);

      // Handle recognized events (final results)
      this.recognizer.recognized = (s: any, e: any) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          const result: SpeechRecognitionResult = {
            text: e.result.text,
            isFinal: true
          };
          this.recognitionResultSubject.next(result);
        } else if (e.result.reason === SpeechSDK.ResultReason.NoMatch) {
          console.log('No speech could be recognized');
        }
      };

      // Handle recognizing events (interim results)
      this.recognizer.recognizing = (s: any, e: any) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizingSpeech) {
          const result: SpeechRecognitionResult = {
            text: e.result.text,
            isFinal: false
          };
          this.recognitionResultSubject.next(result);
        }
      };

      // Handle errors
      this.recognizer.canceled = (s: any, e: any) => {
        let errorMsg = 'Speech recognition was canceled';
        if (e.reason === SpeechSDK.CancellationReason.Error) {
          errorMsg = `Error: ${e.errorDetails}`;
          console.error('Speech recognition error:', e.errorDetails);
        }
        this.recognitionErrorSubject.next(errorMsg);
        this.isRecognizing = false;
      };

      // Handle session stopped
      this.recognizer.sessionStopped = (s: any, e: any) => {
        console.log('Speech recognition session stopped');
        this.isRecognizing = false;
        this.recognitionEndedSubject.next();
      };

      // Start continuous recognition
      this.recognizer.startContinuousRecognitionAsync(
        () => {
          console.log('Speech recognition started');
          this.isRecognizing = true;
        },
        (error: any) => {
          console.error('Error starting speech recognition:', error);
          this.recognitionErrorSubject.next(`Failed to start recognition: ${error}`);
          this.isRecognizing = false;
        }
      );
    } catch (error) {
      console.error('Error in startRecognition:', error);
      this.recognitionErrorSubject.next(`Error starting recognition: ${error}`);
      this.isRecognizing = false;
    }
  }

  /**
   * Stop speech recognition
   */
  public async stopRecognition(): Promise<void> {
    if (!this.isRecognizing || !this.recognizer) {
      return;
    }

    try {
      this.recognizer.stopContinuousRecognitionAsync(
        () => {
          console.log('Speech recognition stopped');
          this.isRecognizing = false;
          this.recognitionEndedSubject.next();
        },
        (error: any) => {
          console.error('Error stopping speech recognition:', error);
          this.recognitionErrorSubject.next(`Failed to stop recognition: ${error}`);
        }
      );
    } catch (error) {
      console.error('Error in stopRecognition:', error);
      this.recognitionErrorSubject.next(`Error stopping recognition: ${error}`);
    }
  }

  /**
   * Check if recognition is currently active
   */
  public isRecognitionActive(): boolean {
    return this.isRecognizing;
  }

  /**
   * Dispose of resources
   */
  public dispose(): void {
    if (this.recognizer) {
      this.recognizer.close();
      this.recognizer = null;
    }
    if (this.audioConfig) {
      this.audioConfig.close();
      this.audioConfig = null;
    }
    this.isRecognizing = false;
    this.isInitialized = false;
  }
}
